import React, { useState, useMemo, useCallback } from "react";
import { motion } from "framer-motion";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  ReferenceDot,
} from "recharts";

import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { Badge } from "../components/ui/badge";

import {
  Calculator,
  BookOpen,
  Lightbulb,
  AlertCircle,
  ArrowRight,
  Copy,
  Check,
  RotateCcw,
  Target,
  Info,
  TrendingUp,
  TrendingDown,
  Waves,
} from 'lucide-react';

// Copy Button Component
const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  return (
    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={handleCopy}>
      {copied ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />}
    </Button>
  );
};

// Solve quadratic equation
const solveQuadratic = (a, b, c) => {
  if (a === 0) {
    // Linear equation
    if (b === 0) {
      if (c === 0) return { type: 'infinite', roots: [], message: 'All real numbers are solutions (0 = 0)' };
      return { type: 'none', roots: [], message: 'No solution (constant ≠ 0)' };
    }
    return { type: 'linear', roots: [-c / b], message: `Linear equation: x = ${(-c / b).toFixed(4)}` };
  }
  
  const discriminant = b * b - 4 * a * c;
  
  if (discriminant > 0) {
    const x1 = (-b + Math.sqrt(discriminant)) / (2 * a);
    const x2 = (-b - Math.sqrt(discriminant)) / (2 * a);
    return { 
      type: 'two-real', 
      roots: [Math.min(x1, x2), Math.max(x1, x2)], 
      discriminant,
      message: 'Two distinct real roots' 
    };
  } else if (Math.abs(discriminant) < 1e-10) {
    const x = -b / (2 * a);
    return { 
      type: 'one-real', 
      roots: [x], 
      discriminant: 0,
      message: 'One repeated real root (double root)' 
    };
  } else {
    const realPart = -b / (2 * a);
    const imaginaryPart = Math.sqrt(-discriminant) / (2 * a);
    return { 
      type: 'complex', 
      roots: [], 
      realPart,
      imaginaryPart: Math.abs(imaginaryPart),
      discriminant,
      message: 'Two complex conjugate roots' 
    };
  }
};

// Generate parabola data
const generateParabolaData = (a, b, c, xRange, steps = 150) => {
  const data = [];
  const step = (xRange[1] - xRange[0]) / steps;
  
  for (let x = xRange[0]; x <= xRange[1]; x += step) {
    const y = a * x * x + b * x + c;
    if (isFinite(y) && Math.abs(y) < 100) {
      data.push({ x: parseFloat(x.toFixed(3)), y: parseFloat(y.toFixed(4)) });
    }
  }
  return data;
};

// Calculate vertex
const getVertex = (a, b, c) => {
  const x = -b / (2 * a);
  const y = a * x * x + b * x + c;
  return { x, y };
};

// Graph Explanation Component
const GraphExplanation = ({ a, b, c, solution }) => {
  const vertex = a !== 0 ? getVertex(a, b, c) : null;
  const direction = a > 0 ? 'upward' : 'downward';
  
  return (
    <div className="space-y-3 p-4 bg-muted/30 rounded-xl" data-testid="graph-explanation">
      <h4 className="font-semibold text-foreground flex items-center gap-2">
        <Waves className="w-4 h-4 text-primary" />
        Parabola Analysis
      </h4>
      
      <div className="space-y-2 text-sm">
        {vertex && (
          <div className="flex items-start gap-2">
            <Target className="w-4 h-4 text-violet-500 mt-0.5 flex-shrink-0" />
            <div>
              <span className="font-medium">Vertex:</span>
              <p className="text-muted-foreground">
                ({vertex.x.toFixed(2)}, {vertex.y.toFixed(2)}) — This is the {a > 0 ? 'minimum' : 'maximum'} point of the parabola.
              </p>
            </div>
          </div>
        )}
        
        <div className="flex items-start gap-2">
          {a > 0 ? <TrendingUp className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" /> : <TrendingDown className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />}
          <div>
            <span className="font-medium">Direction:</span>
            <p className="text-muted-foreground">
              Opens {direction} because a = {a} is {a > 0 ? 'positive' : 'negative'}.
            </p>
          </div>
        </div>
        
        <div className="flex items-start gap-2">
          <Lightbulb className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
          <div>
            <span className="font-medium">Roots (x-intercepts):</span>
            <p className="text-muted-foreground">
              {solution.type === 'two-real' 
                ? `The parabola crosses the x-axis at x = ${solution.roots[0].toFixed(2)} and x = ${solution.roots[1].toFixed(2)}.`
                : solution.type === 'one-real'
                ? `The parabola touches the x-axis at exactly one point: x = ${solution.roots[0].toFixed(2)} (vertex on x-axis).`
                : solution.type === 'complex'
                ? 'The parabola never crosses the x-axis (entirely above or below it).'
                : 'Linear or degenerate case.'}
            </p>
          </div>
        </div>
        
        <div className="flex items-start gap-2">
          <Info className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
          <div>
            <span className="font-medium">Y-intercept:</span>
            <p className="text-muted-foreground">
              The parabola crosses the y-axis at y = {c} (when x = 0).
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

function QuadraticSolver() {
  // ax² + bx + c = 0
  const [a, setA] = useState(1);
  const [b, setB] = useState(-5);
  const [c, setC] = useState(6);
  
  const [expandedSteps, setExpandedSteps] = useState(['step-1']);

  const solution = useMemo(() => {
    return solveQuadratic(a, b, c);
  }, [a, b, c]);

  const vertex = useMemo(() => {
    return a !== 0 ? getVertex(a, b, c) : { x: 0, y: c };
  }, [a, b, c]);

  // Dynamic x-range based on roots and vertex
  const xRange = useMemo(() => {
    let minX = vertex.x - 5;
    let maxX = vertex.x + 5;
    
    if (solution.roots && solution.roots.length > 0) {
      const rootMin = Math.min(...solution.roots);
      const rootMax = Math.max(...solution.roots);
      minX = Math.min(minX, rootMin - 2);
      maxX = Math.max(maxX, rootMax + 2);
    }
    
    return [minX, maxX];
  }, [vertex, solution]);

  const parabolaData = useMemo(() => {
    return a !== 0 ? generateParabolaData(a, b, c, xRange) : [];
  }, [a, b, c, xRange]);

  const handleReset = useCallback(() => {
    setA(1); setB(-5); setC(6);
  }, []);

  const discriminant = b * b - 4 * a * c;

  return (
    <div className="container mx-auto px-4 py-8" data-testid="quadratic-calculator">
      {/* Page Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex items-center gap-3 mb-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500/20 to-purple-500/20 flex items-center justify-center">
            <Waves className="w-6 h-6 text-violet-600" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Quadratic Formula Solver</h1>
            <p className="text-muted-foreground">Solve ax² + bx + c = 0 with parabola visualization</p>
          </div>
        </div>
      </motion.div>

      {/* Main Content Grid */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left Column - Calculator Panel */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
          className="lg:sticky lg:top-24 lg:self-start"
        >
          <Card className="rounded-2xl p-6 border bg-card">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <Calculator className="w-5 h-5 text-violet-600" />
                <span className="font-semibold text-foreground">Enter Coefficients</span>
              </div>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-8 w-8" 
                onClick={handleReset} 
                title="Reset"
                data-testid="reset-btn"
              >
                <RotateCcw className="w-4 h-4" />
              </Button>
            </div>

            {/* Equation Display */}
            <div className="p-4 rounded-xl bg-violet-500/10 border border-violet-500/20 mb-4">
              <p className="text-center font-mono text-lg text-foreground">
                <span className="text-violet-600">{a !== 0 ? (a === 1 ? '' : a === -1 ? '-' : a) : ''}</span>
                {a !== 0 && <span>x²</span>}
                {b !== 0 && (
                  <>
                    <span className="text-violet-600"> {b > 0 && a !== 0 ? '+' : ''}{b === 1 ? '' : b === -1 ? '-' : b}</span>
                    <span>x</span>
                  </>
                )}
                {c !== 0 && (
                  <span className="text-violet-600"> {c > 0 ? '+' : ''}{c}</span>
                )}
                <span> = 0</span>
              </p>
            </div>

            {/* Coefficient Inputs */}
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs text-muted-foreground mb-1 block">a (x² coef)</Label>
                  <Input
                    type="number"
                    value={a}
                    onChange={(e) => setA(parseFloat(e.target.value) || 0)}
                    className="text-center font-mono"
                    data-testid="a-input"
                  />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground mb-1 block">b (x coef)</Label>
                  <Input
                    type="number"
                    value={b}
                    onChange={(e) => setB(parseFloat(e.target.value) || 0)}
                    className="text-center font-mono"
                    data-testid="b-input"
                  />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground mb-1 block">c (constant)</Label>
                  <Input
                    type="number"
                    value={c}
                    onChange={(e) => setC(parseFloat(e.target.value) || 0)}
                    className="text-center font-mono"
                    data-testid="c-input"
                  />
                </div>
              </div>

              {/* Quick Examples */}
              <div>
                <Label className="text-xs text-muted-foreground mb-2 block">Quick Examples</Label>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-xs"
                    onClick={() => { setA(1); setB(-5); setC(6); }}
                  >
                    2 Real Roots
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-xs"
                    onClick={() => { setA(1); setB(-4); setC(4); }}
                  >
                    1 Root
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-xs"
                    onClick={() => { setA(1); setB(0); setC(4); }}
                  >
                    Complex
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-xs"
                    onClick={() => { setA(2); setB(7); setC(-4); }}
                  >
                    2x²+7x-4
                  </Button>
                </div>
              </div>

              {/* Discriminant */}
              <div className="p-3 rounded-lg bg-muted/50">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Discriminant (Δ = b² - 4ac)</span>
                  <Badge variant={discriminant > 0 ? 'default' : discriminant === 0 ? 'secondary' : 'destructive'} className="font-mono">
                    {discriminant.toFixed(2)}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {discriminant > 0 ? 'Δ > 0: Two distinct real roots' : discriminant === 0 ? 'Δ = 0: One repeated root' : 'Δ < 0: Two complex roots'}
                </p>
              </div>

              {/* Results */}
              <div className="pt-4 border-t border-border space-y-3">
                <Label className="text-sm font-medium text-foreground">Solutions</Label>
                <motion.div
                  className={`rounded-xl p-4 ${
                    solution.type === 'two-real' || solution.type === 'one-real' || solution.type === 'linear'
                      ? 'bg-violet-500/10 border border-violet-500/30'
                      : solution.type === 'complex'
                      ? 'bg-blue-500/10 border border-blue-500/30'
                      : 'bg-red-500/10 border border-red-500/30'
                  }`}
                  initial={{ scale: 0.95 }}
                  animate={{ scale: 1 }}
                  key={`${a}-${b}-${c}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <Badge variant="secondary" className="text-xs">
                      {solution.message}
                    </Badge>
                    {solution.roots.length > 0 && (
                      <CopyButton text={solution.roots.map(r => r.toFixed(4)).join(', ')} />
                    )}
                  </div>
                  
                  {solution.type === 'two-real' && (
                    <div className="space-y-1">
                      <p className="font-mono text-lg font-semibold text-foreground">
                        x₁ = <span className="text-violet-600">{solution.roots[0].toFixed(4)}</span>
                      </p>
                      <p className="font-mono text-lg font-semibold text-foreground">
                        x₂ = <span className="text-violet-600">{solution.roots[1].toFixed(4)}</span>
                      </p>
                    </div>
                  )}
                  
                  {(solution.type === 'one-real' || solution.type === 'linear') && (
                    <p className="font-mono text-lg font-semibold text-foreground">
                      x = <span className="text-violet-600">{solution.roots[0].toFixed(4)}</span>
                    </p>
                  )}
                  
                  {solution.type === 'complex' && (
                    <div className="space-y-1">
                      <p className="font-mono text-lg font-semibold text-foreground">
                        x₁ = <span className="text-blue-600">{solution.realPart.toFixed(4)} + {solution.imaginaryPart.toFixed(4)}i</span>
                      </p>
                      <p className="font-mono text-lg font-semibold text-foreground">
                        x₂ = <span className="text-blue-600">{solution.realPart.toFixed(4)} - {solution.imaginaryPart.toFixed(4)}i</span>
                      </p>
                    </div>
                  )}
                  
                  {(solution.type === 'none' || solution.type === 'infinite') && (
                    <p className="text-muted-foreground">{solution.message}</p>
                  )}
                </motion.div>
              </div>
            </div>
          </Card>
        </motion.div>

        {/* Center/Right Column - Visualization & Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Visualization Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card className="rounded-2xl p-6 border bg-card">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-semibold text-foreground flex items-center gap-2">
                  <Info className="w-4 h-4 text-violet-600" />
                  Parabola Graph
                  <Badge variant="secondary" className="ml-2 text-xs">Interactive</Badge>
                </h2>
              </div>

              {a === 0 ? (
                <div className="h-[350px] flex items-center justify-center bg-muted/30 rounded-xl border">
                  <p className="text-muted-foreground">Set a ≠ 0 to see the parabola. Currently showing a linear equation.</p>
                </div>
              ) : (
                <div className="h-[350px] bg-muted/30 rounded-xl p-4 border" data-testid="quadratic-graph">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={parabolaData} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis 
                        dataKey="x" 
                        type="number"
                        domain={[xRange[0], xRange[1]]}
                        tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                        tickFormatter={(v) => v.toFixed(1)}
                      />
                      <YAxis 
                        type="number"
                        domain={['auto', 'auto']}
                        tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: 'hsl(var(--card))',
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px',
                          fontSize: '12px'
                        }}
                        formatter={(value, name) => [value.toFixed(4), 'y']}
                        labelFormatter={(label) => `x = ${label}`}
                      />
                      <ReferenceLine x={0} stroke="hsl(var(--muted-foreground))" strokeWidth={1} />
                      <ReferenceLine y={0} stroke="hsl(var(--muted-foreground))" strokeWidth={1} />
                      
                      {/* Parabola */}
                      <Line
                        type="monotone"
                        dataKey="y"
                        stroke="#8b5cf6"
                        dot={false}
                        strokeWidth={2.5}
                        name="f(x)"
                      />
                      
                      {/* Vertex */}
                      <ReferenceDot
                        x={vertex.x}
                        y={vertex.y}
                        r={7}
                        fill="#8b5cf6"
                        stroke="white"
                        strokeWidth={2}
                      />
                      
                      {/* Roots */}
                      {solution.roots && solution.roots.map((root, i) => (
                        <ReferenceDot
                          key={i}
                          x={root}
                          y={0}
                          r={6}
                          fill="#10b981"
                          stroke="white"
                          strokeWidth={2}
                        />
                      ))}
                      
                      {/* Axis of symmetry */}
                      <ReferenceLine
                        x={vertex.x}
                        stroke="#8b5cf6"
                        strokeDasharray="5 5"
                        strokeWidth={1}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
              
              <div className="flex justify-center gap-6 mt-4">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-1 rounded bg-violet-500" />
                  <span className="text-sm text-muted-foreground">Parabola</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-violet-500" />
                  <span className="text-sm text-muted-foreground">Vertex</span>
                </div>
                {solution.roots && solution.roots.length > 0 && (
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-emerald-500" />
                    <span className="text-sm text-muted-foreground">Roots</span>
                  </div>
                )}
              </div>
              
              {/* Graph Explanation */}
              {a !== 0 && (
                <div className="mt-4">
                  <GraphExplanation a={a} b={b} c={c} solution={solution} />
                </div>
              )}
            </Card>
          </motion.div>

          {/* What is Quadratic Equation */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
          >
            <Card className="rounded-2xl p-6 border bg-card">
              <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-violet-600" />
                What is a Quadratic Equation?
              </h2>
              
              <div className="space-y-4">
                <p className="text-muted-foreground leading-relaxed">
                  A quadratic equation is a second-degree polynomial equation of the form <span className="font-mono bg-muted px-2 py-0.5 rounded">ax² + bx + c = 0</span>, 
                  where a ≠ 0. The graph of a quadratic function is a U-shaped curve called a <strong>parabola</strong>.
                </p>
                
                <div className="p-4 rounded-xl bg-violet-500/10 border border-violet-500/20">
                  <h3 className="font-bold text-violet-600 dark:text-violet-400 mb-2">The Quadratic Formula</h3>
                  <p className="font-mono text-lg text-center text-foreground">
                    x = (-b ± √(b² - 4ac)) / 2a
                  </p>
                  <p className="text-sm text-muted-foreground mt-2 text-center">
                    This formula gives us all solutions to any quadratic equation.
                  </p>
                </div>
                
                <div className="grid sm:grid-cols-3 gap-4">
                  <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
                    <h3 className="font-bold text-emerald-600 dark:text-emerald-400 mb-2">Δ &gt; 0</h3>
                    <p className="text-sm text-muted-foreground">Two distinct real roots. Parabola crosses x-axis at two points.</p>
                  </div>
                  <div className="p-4 rounded-xl bg-yellow-500/10 border border-yellow-500/20">
                    <h3 className="font-bold text-yellow-600 dark:text-yellow-400 mb-2">Δ = 0</h3>
                    <p className="text-sm text-muted-foreground">One repeated root (double root). Parabola touches x-axis at vertex.</p>
                  </div>
                  <div className="p-4 rounded-xl bg-blue-500/10 border border-blue-500/20">
                    <h3 className="font-bold text-blue-600 dark:text-blue-400 mb-2">Δ &lt; 0</h3>
                    <p className="text-sm text-muted-foreground">Two complex conjugate roots. Parabola doesn't cross x-axis.</p>
                  </div>
                </div>
              </div>
            </Card>
          </motion.div>

          {/* Step-by-Step Solution */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card className="rounded-2xl p-6 border bg-card">
              <h2 className="font-semibold text-foreground flex items-center gap-2 mb-4">
                <BookOpen className="w-4 h-4 text-violet-600" />
                Step-by-Step Solution
              </h2>

              <Accordion type="multiple" value={expandedSteps} onValueChange={setExpandedSteps}>
                <AccordionItem value="step-1" className="border-b border-border">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 rounded-full bg-violet-600 text-white flex items-center justify-center text-xs font-bold">1</span>
                      <span className="font-medium">Identify coefficients</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="pl-11 space-y-3">
                      <p className="text-muted-foreground">From the equation: {a}x² + {b}x + {c} = 0</p>
                      <div className="bg-secondary/50 rounded-lg p-4 font-mono text-sm space-y-1">
                        <p>a = <span className="text-violet-600 font-bold">{a}</span> (coefficient of x²)</p>
                        <p>b = <span className="text-violet-600 font-bold">{b}</span> (coefficient of x)</p>
                        <p>c = <span className="text-violet-600 font-bold">{c}</span> (constant term)</p>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="step-2" className="border-b border-border">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 rounded-full bg-violet-600 text-white flex items-center justify-center text-xs font-bold">2</span>
                      <span className="font-medium">Calculate the discriminant</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="pl-11 space-y-3">
                      <p className="text-muted-foreground">Δ = b² - 4ac</p>
                      <div className="bg-secondary/50 rounded-lg p-4">
                        <p className="font-mono">Δ = ({b})² - 4·({a})·({c})</p>
                        <p className="font-mono">Δ = {b * b} - {4 * a * c}</p>
                        <p className="font-mono font-bold text-violet-600">Δ = {discriminant}</p>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {discriminant > 0 ? '✓ Δ > 0: Two distinct real solutions exist.' : 
                         discriminant === 0 ? '✓ Δ = 0: One repeated solution exists.' :
                         '⚠️ Δ < 0: No real solutions (complex roots).'}
                      </p>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="step-3" className="border-0">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 rounded-full bg-violet-600 text-white flex items-center justify-center text-xs font-bold">3</span>
                      <span className="font-medium">Apply the quadratic formula</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="pl-11 space-y-4">
                      <div className="bg-secondary/50 rounded-lg p-4 space-y-2">
                        <p className="font-mono">x = (-b ± √Δ) / 2a</p>
                        <p className="font-mono">x = (-({b}) ± √{discriminant}) / (2·{a})</p>
                        <p className="font-mono">x = ({-b} ± {discriminant >= 0 ? Math.sqrt(discriminant).toFixed(4) : `√(${discriminant})`}) / {2 * a}</p>
                      </div>
                      
                      {solution.type === 'two-real' && (
                        <div className="bg-violet-500/10 border border-violet-500/30 rounded-lg p-4 space-y-2">
                          <p className="font-mono">x₁ = ({-b} + {Math.sqrt(discriminant).toFixed(4)}) / {2 * a} = <span className="text-violet-600 font-bold">{solution.roots[0].toFixed(4)}</span></p>
                          <p className="font-mono">x₂ = ({-b} - {Math.sqrt(discriminant).toFixed(4)}) / {2 * a} = <span className="text-violet-600 font-bold">{solution.roots[1].toFixed(4)}</span></p>
                        </div>
                      )}
                      
                      {solution.type === 'one-real' && (
                        <div className="bg-violet-500/10 border border-violet-500/30 rounded-lg p-4">
                          <p className="font-mono">x = {-b} / {2 * a} = <span className="text-violet-600 font-bold">{solution.roots[0].toFixed(4)}</span></p>
                          <p className="text-sm text-muted-foreground mt-2">Since Δ = 0, both solutions are the same (double root).</p>
                        </div>
                      )}
                      
                      {solution.type === 'complex' && (
                        <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 space-y-2">
                          <p className="text-muted-foreground mb-2">Since Δ &lt; 0, we work with imaginary numbers:</p>
                          <p className="font-mono">√Δ = √({discriminant}) = {solution.imaginaryPart.toFixed(4)}i</p>
                          <p className="font-mono">x₁ = <span className="text-blue-600 font-bold">{solution.realPart.toFixed(4)} + {solution.imaginaryPart.toFixed(4)}i</span></p>
                          <p className="font-mono">x₂ = <span className="text-blue-600 font-bold">{solution.realPart.toFixed(4)} - {solution.imaginaryPart.toFixed(4)}i</span></p>
                        </div>
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </Card>
          </motion.div>

          {/* Key Formulas */}
          <Card className="rounded-2xl p-6 border bg-card">
            <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-violet-600" />
              Key Formulas & Properties
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { name: 'Quadratic Formula', formula: 'x = (-b ± √(b²-4ac)) / 2a' },
                { name: 'Discriminant', formula: 'Δ = b² - 4ac' },
                { name: 'Vertex x-coordinate', formula: 'x = -b / 2a' },
                { name: 'Vertex y-coordinate', formula: 'y = c - b² / 4a' },
                { name: 'Sum of roots', formula: 'x₁ + x₂ = -b / a' },
                { name: 'Product of roots', formula: 'x₁ · x₂ = c / a' },
              ].map((item, i) => (
                <div key={i} className="bg-muted/30 rounded-xl p-4">
                  <span className="text-xs text-muted-foreground block mb-1">{item.name}</span>
                  <code className="font-mono text-sm text-foreground">{item.formula}</code>
                </div>
              ))}
            </div>
          </Card>

          {/* Common Mistakes */}
          <Card className="rounded-2xl p-6 bg-destructive/5 border border-destructive/20">
            <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-destructive" />
              Common Mistakes to Avoid
            </h2>
            <ul className="space-y-3">
              {[
                'Forgetting to make the equation equal to zero before applying the formula',
                'Sign errors when substituting negative values for b or c',
                'Forgetting that ± means you need to calculate BOTH solutions',
                'Dividing only the first term by 2a instead of the entire numerator',
                'Not recognizing when a = 0 (which makes it a linear equation)',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-destructive mt-2 flex-shrink-0" />
                  <span className="text-muted-foreground">{item}</span>
                </li>
              ))}
            </ul>
          </Card>

          {/* Real World Applications */}
          <Card className="rounded-2xl p-6 border bg-card">
            <h2 className="text-xl font-bold text-foreground mb-4">Real-World Applications</h2>
            <ul className="space-y-3">
              {[
                'Physics: Projectile motion (calculating maximum height and landing position)',
                'Engineering: Designing parabolic reflectors for satellite dishes and headlights',
                'Economics: Profit maximization and cost minimization problems',
                'Architecture: Designing arches and bridges with parabolic curves',
                'Sports: Analyzing the trajectory of balls in basketball, golf, and football',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <ArrowRight className="w-4 h-4 text-violet-600 mt-1 flex-shrink-0" />
                  <span className="text-muted-foreground">{item}</span>
                </li>
              ))}
            </ul>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default QuadraticSolver;
